<template>
  <div class="projet-part">
    <h2 id="finalisation">{{ $t("projet.inscription.finalisation-inscription.titre") }}</h2>

    <InputWithValidation
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="required"
      type="textarea"
      :label="$t('projet.inscription.vue-ensemble.edd.how-to-know-us')"
      :model-value="projetAnnuel.cmtAvezConnuLeDispositif"
      :disabled="readonly"
      maxlength="1400"
      vid="finalisation_cmtAvezConnuLeDispositif"
      @update:model-value="updateCmtavezconnuledispositif"
      @manual-change="$emit('dirtify-project')"
    />

    <div class="finalisation">
      <div class="validation-display">
        <!-- Valid or invalid -->
        <div class="validation-icon">
          <b-icon
            v-if="errorsPerLabel.size"
            type="is-danger"
            size="is-large"
            icon="exclamation-circle"
          />
          <b-icon v-else type="is-success" size="is-large" icon="check-circle" />
        </div>
        <div>
          <div v-if="projetAnnuel.statut == 'ACCEPTE'">
            {{ $t("projet.submit-inscription-validated") }}
          </div>
          <div v-else-if="projetAnnuel.statut != 'BROUILLON'">
            {{ $t("projet.submit-inscription-sent") }}
          </div>
          <div v-else-if="errorsPerLabel.size">
            <span>{{ $t("projet.inscription.finalisation-inscription.invalid") }}</span>
            <ul>
              <li
                v-for="fieldWithError in errorsPerLabel.keys()"
                :key="fieldWithError"
                class="field-in-error"
              >
                <a :href="'#' + fieldWithError">
                  <span class="error-circle">
                    <span>{{ errorsPerLabel.get(fieldWithError) }}</span>
                  </span>
                  {{ $t("projet.inscription." + fieldWithError) }}</a
                >
              </li>
            </ul>
          </div>
          <div v-else>
            <span v-html="$t('projet.inscription.finalisation-inscription.valid')" />

            <b-message v-if="isMissingReferent" type="is-danger">
              {{ $t("projetPage.infoAdministratives.participants.at-least-one-referent") }}
            </b-message>
          </div>

          <!-- Finalisation checkboxes -->
          <div class="checkboxes mb-3">
            <CheckboxWithValidation
              v-if="projetAnnuel.statut == 'BROUILLON'"
              v-model="exactInfosCertified"
              :validation-activated="validationActivated"
              :show-validation-errors="showValidationErrors"
              rules="certified-exact"
              :disabled="readonly"
              vid="finalisation_exactInfosCertified"
              @manual-change="$emit('trigger-validation')"
            >
              {{ $t("projet.inscription.finalisation-inscription.certify") }}
            </CheckboxWithValidation>
            <CheckboxWithValidation
              :validation-activated="validationActivated"
              :show-validation-errors="showValidationErrors"
              :disabled="readonly"
              :model-value="projetAnnuel.recevoirKitAireEducative"
              vid="finalisation_recevoirKitAireEducative"
              @update:model-value="updateRecevoirkitaireeducative"
              @manual-change="$emit('dirtify-project')"
            >
              {{ $t("projet.inscription.finalisation-inscription.kit") }}
            </CheckboxWithValidation>
          </div>
          <!-- Commentaires -->
          <InputWithValidation
            :disabled="readonly"
            :label="$t('projet.inscription.finalisation-inscription.commentaire')"
            :placeholder="$t('projet.inscription.finalisation-inscription.commentaire-placeholder')"
            :validation-activated="validationActivated"
            :model-value="projetAnnuel.commentaireFinal"
            :show-validation-errors="showValidationErrors"
            type="textarea"
            vid="finalisation_commentaireFinal"
            @update:model-value="updateCommentairefinal"
            @manual-change="$emit('dirtify-project')"
          />
        </div>
      </div>

      <RecapCard>
        <div class="recap-text">
          {{ $t("projet.inscription.finalisation-inscription.recap-text-projet") }}
          <span
            v-if="projetAnnuel.natureAireEducative == 'ATE'"
            v-html="$t('projet.inscription.finalisation-inscription.recap-text-ate-type')"
          />
          <span
            v-else
            v-html="$t('projet.inscription.finalisation-inscription.recap-text-ame-type')"
          />
          <span v-if="projetAnnuel.etablissements.length > 0">
            {{ $t("projet.inscription.finalisation-inscription.recap-text-at") }}
            <strong>{{ projetAnnuel.etablissements.map((et) => et.nom).join(",") }}</strong>
          </span>
        </div>
        <div v-if="projetAnnuel.financements.length" class="recap-financements">
          {{
            $t("projet.inscription.finalisation-inscription.financement-declared", [
              projetAnnuel.financements.length,
            ])
          }}
        </div>
        <div class="recap-invited">
          {{ $t("projet.inscription.finalisation-inscription.invited") }}
          <ul>
            <li v-if="projetAnnuel.enseignants.length">
              {{
                $t("projet.inscription.finalisation-inscription.invited-enseignants", [
                  projetAnnuel.enseignants.length,
                ])
              }}
            </li>
            <li v-if="projetAnnuel.referents.length">
              {{
                $t("projet.inscription.finalisation-inscription.invited-referents", [
                  projetAnnuel.referents.length,
                ])
              }}
            </li>
          </ul>
        </div>
      </RecapCard>
    </div>
  </div>
</template>

<script lang="ts">
import { BMessage, BIcon } from "buefy"

import { ProjetInscriptionDTO } from "@/model/bean/GeneratedDTOs"
import { defineComponent } from "vue"
import CheckboxWithValidation from "@/views/inputs/CheckBoxWithValidation.vue"
import { ProjetAnnualValidation } from "../commons/ProjectValidationAndSaveHandler.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import RecapCard from "@/views/projects_pp/RecapCard.vue"

export default defineComponent({
  name: "ProjetInscriptionFinalisation",
  components: {
    CheckboxWithValidation,
    InputWithValidation,
    RecapCard,
    BMessage,
    BIcon,
  },
  props: {
    projetAnnuel: {
      type: Object as () => ProjetInscriptionDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    projetValidation: {
      type: Object as () => ProjetAnnualValidation,
      required: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["dirtify-project", "trigger-validation", "projet-annuel-updated"],
  data() {
    return {
      errorsPerLabel: new Map<string, number>(),
      exactInfosCertified: false,
      isMissingReferent: false,
    }
  },
  watch: {
    projetValidation(): void {
      this.validationChanged()
    },
  },
  methods: {
    updateCommentairefinal(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        commentaireFinal: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    updateRecevoirkitaireeducative(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        recevoirKitAireEducative: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    updateCmtavezconnuledispositif(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        cmtAvezConnuLeDispositif: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    validationChanged(): void {
      // Sort all invalid & empty fields by sections, calculating total for each subsection
      this.errorsPerLabel = new Map<string, number>()
      const allErrors = Array.from(this.projetValidation.invalidFields.keys())
      const allWarnings = Array.from(this.projetValidation.emptyFields.keys())

      allErrors.forEach((field: string) => {
        if (field != "finalisation") {
          const numberOfError = this.projetValidation.invalidFields.get(field)?.length
          this.errorsPerLabel.set(field, numberOfError ? numberOfError : 0)
        }
      })
      allWarnings.forEach((field: string) => {
        const numberOfWarnings = this.projetValidation.emptyFields.get(field)?.length
        const numberOfError = this.projetValidation.invalidFields.get(field)?.length
        let total = (numberOfError ? numberOfError : 0) + (numberOfWarnings ? numberOfWarnings : 0)
        // If the only remaining issue is that referent is missing, do not forbid inscription, just display a custom warning
        if (field == "participants") {
          const allParticipantsErrors = this.projetValidation.emptyFields.get(field)
          if (allParticipantsErrors) {
            const missingReferentsError = Array.from(allParticipantsErrors).filter((issue) => {
              return issue.field == "participants_referents"
            })
            if (missingReferentsError && missingReferentsError.length > 0) {
              if (allParticipantsErrors.length == 1) {
                this.isMissingReferent = true
              }
              total = total - 1
            }
          }
        }
        if (total > 0) {
          this.errorsPerLabel.set(field, total)
        }
      })
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.finalisation {
  display: flex;
  flex-wrap: wrap;

  .validation-display {
    box-sizing: border-box;
    flex: 1 1 45%;
    min-width: 500px;
    display: flex;
    justify-content: space-between;
    margin-right: 1rem;
    margin-bottom: 1rem;
  }

  .BookmarkCard.recap {
    box-sizing: border-box;
    flex: 1 1 45%;
    min-width: 500px;
    height: auto;
    margin-bottom: 1rem;
  }

  .validation-icon {
    display: flex;
    justify-content: center;
    width: 120px;
    height: 120px;

    i {
      width: 120px;
      height: 120px;

      .is-danger {
        color: $red-errors;
      }

      .is-success {
        color: $ofb-vert-2;
      }
    }
  }

  .field-in-error {
    padding-left: 10px;
    padding-top: 10px;

    a {
      color: black;

      &:hover {
        color: $red-errors;
        font-weight: bolder;
      }
    }

    .error-circle {
      border-radius: 50%;
      display: block;
      width: 22px;
      height: 22px;
      background-color: $red-errors;
      float: left;
      margin-left: 5px;
      margin-right: 5px;

      span {
        color: white;
        font-weight: bolder;
        padding-left: 6px;
        margin-right: 6px;
        font-size: 16px;
      }
    }
  }

  .checkboxes {
    padding-top: 10px;
  }
}

.recap-text {
  padding-top: 40px;

  span {
    display: inline;
  }
}

.recap-invited {
  padding-top: 20px;
  width: 60%;

  ul {
    list-style: disc outside;
    margin-left: 20px;
    padding-left: 0;
  }
}
</style>
